import React from 'react';
import { useDidMount, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { ComponentInfo, PdfViewerDrawer } from '@components';
import { Cloudware } from '@icons';
import { useDrawer, useFetchDocument } from '@hooks';
import {
	Box,
	SignDocCard,
	Spacer,
	Typography,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import {
	SignDocCardSignedMode,
	SignDocCardUnsignedMode,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system/dist/organisms/SignDocCard/SignDocCard.models';
import { rem } from '@helpers';
import { Anything } from '@types';
import { useNavigationStep, useSelectedAccount, useServiceResponse, useSoftwareTerms } from '@store';
import { ROUTES } from '@navigation';
import { PromoCodeStatus } from '@api/izibizi-xp';

export const CloudwareDocumentPlaceholder: React.FC = () => {
	const t = useTranslation();
	const { palette } = useTheme();
	const cloudwareTermsDrawerName = 'cloudwareTerms';
	const { acceptTerms, acceptDebit, refuseDebit, termsWereAccepted, debitWasAccepted } = useSoftwareTerms();
	const { show: showPdfDrawer, hide: hidePdfDrawer } = useDrawer(cloudwareTermsDrawerName);
	const { retrievePromoCodeDetail } = useServiceResponse();

	const defineDocumentAccordingPromotion = () => {
		let documentValue: string;
		const codeStatus = retrievePromoCodeDetail?.promoCodeStatus;
		const isSubCodeValid = codeStatus === PromoCodeStatus.ACTIVE ? true : false;
		if (isSubCodeValid) {
			return (documentValue = t('document.conditionsTermsSoftwareWithPromotion.id'));
		} else return (documentValue = t('document.conditionsTermsSoftware.id'));
	};
	const { base64SoftwareDocument: base64Terms, fetchSoftwareDocument } = useFetchDocument({
		reference: defineDocumentAccordingPromotion(),
	});
	const { selectedAccount } = useSelectedAccount();
	const { current: currentRoute } = useNavigationStep();

	const handleOnClickReadAndAcceptDocument = () => {
		showPdfDrawer();
	};

	const handleOnClickCheckAcceptDebit = (checked: boolean) => {
		if (debitWasAccepted !== checked) {
			checked ? acceptDebit() : refuseDebit();
		}
	};

	const handleOnClickAcceptTerms = () => {
		acceptTerms();
		hidePdfDrawer();
	};

	const signedProps: SignDocCardSignedMode = {
		mode: 'signed',
		linkProps: {
			middleContent: t('general.document.actions.consult'),
			onPress: handleOnClickReadAndAcceptDocument,
		},
	};

	const unsignedProps: SignDocCardUnsignedMode = {
		mode: 'unsigned',
		buttonProps: {
			label: t('general.document.actions.readAndAccept'),
			onPress: handleOnClickReadAndAcceptDocument,
		},
		image: {
			type: 'avatar',
			avatarProps: {
				source: Cloudware,
				backgroundColor: 'transparent',
			},
		},
	};

	const appearanceTitle = 'microcopy-100-04';
	const appearanceDescription = 'microcopy-100-02';

	useDidMount(() => {
		fetchSoftwareDocument();
	});

	return (
		<ComponentInfo name="CloudwareDocumentPlaceholder">
			<Box styles={{ marginTop: rem(4), flexDirection: 'column' }}>
				<SignDocCard
					{...(termsWereAccepted ? signedProps : unsignedProps)}
					title={t('general.brands.cloudware')}
					description={t('placeholders.cloudware.documentContract')}
					bottomContent={{
						message: t('placeholders.cloudware.acceptDebit'),
						checkBoxProps: {
							checked: !!debitWasAccepted,
							onChange: handleOnClickCheckAcceptDebit,
							disabled: currentRoute === ROUTES.subscriptionResumeAndAuthorization,
						},
						icon: {
							tooltip: {
								containerProps: { maxWidth: rem(295) } as Anything,
								customContent: (
									<Box styles={{ flexDirection: 'column' }}>
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceTitle}
										>
											{t('placeholders.cloudware.tooltip.title')}
										</Typography>
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceDescription}
										>
											{selectedAccount?.account.iban}
										</Typography>
										<Spacer vertical={rem(16)} />
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceTitle}
										>
											{t('placeholders.cloudware.tooltip.enterprize')}
										</Typography>
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceDescription}
										>
											{selectedAccount?.companyName}
										</Typography>
										<Spacer vertical={rem(16)} />
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceTitle}
										>
											{t('placeholders.cloudware.tooltip.cloudwareId.label')}
										</Typography>
										<Typography
											alternativeStyleProperties={{ color: palette.global.ui18 } as Anything}
											appearance={appearanceDescription}
										>
											{t('placeholders.cloudware.tooltip.cloudwareId.id')}
										</Typography>
									</Box>
								),
							},
						},
					}}
				/>
				{base64Terms && (
					<PdfViewerDrawer
						title={t('placeholders.cloudwareDocument.terms.title')}
						subtitle={t('general.pdfViewer.attentionRead')}
						base64={base64Terms}
						drawerUniqueName={cloudwareTermsDrawerName}
						handleOnClickAcceptTerms={handleOnClickAcceptTerms}
						wasAccepted={termsWereAccepted}
					/>
				)}
			</Box>
		</ComponentInfo>
	);
};
